import React, { useState, useRef } from "react";
import { useMutation, gql, useQuery } from "@apollo/client";
import RichTextEditor from "react-rte";
import SimpleDateInput from "../../components/forms/SimpleDateInput";
import TitleHeading from "../../components/TitleHeading";
import SimpleSelect from "../../components/forms/SimpleSelect";
import SimpleButton from "../../components/forms/SimpleButton";
import CreatableSelect from "react-select/creatable";
import _ from "lodash";
import { Link, useParams, useLocation } from "react-router-dom";
import { useAuth0 } from "../../react-auth0-spa";

const MEETING_STATUSES = gql`
  query GetMeetingStatuses {
    inventory_meeting_note_status {
      id
      name: meeting_note_status_name
    }
  }
`;

const CLIENT = gql`
  query GetClientInfo($client_id: Int!) {
    inventory_account(where: { id: { _eq: $client_id } }) {
      id
      local_name
      account_description
      account_roles {
        role {
          role_name
          id
        }
      }
      account_locations(where: { parent_location_id: { _is_null: true } }) {
        name: location_name
        parent_location_id
        child_locations {
          name: location_name
          address {
            name
          }
          child_locations {
            name: location_name
            address {
              name
            }
            child_locations {
              name: location_name
              address {
                name
              }
            }
          }
        }
      }
    }
  }
`;

const INSERT_MEETING = gql`
  mutation AddMeeting(
    $user_id: String!
    $content: String!
    $contacts: _text!
    $meeting_note_status_id: Int!
    $meeting_date: timestamptz!
    $account_id: Int!
  ) {
    insert_inventory_account_meeting_note_one(
      object: {
        account_id: $account_id
        user_id: $user_id
        meeting_note: {
          data: {
            content: $content
            meeting_note_status_id: $meeting_note_status_id
            contacts: $contacts
            meeting_date: $meeting_date
            user_id: $user_id
            folder: { data: { user_id: $user_id } }
          }
        }
      }
    ) {
      id
    }
  }
`;

const AddMeeting = () => {
  const useRouteQuery = () => new URLSearchParams(useLocation().search);
  let query = useRouteQuery();
  let location = useLocation();
  const selectInputRef = useRef();
  const [successCode, setSuccessCode] = useState(0);
  const { account_id } = useParams();

  const { loading: queryLoading, error: queryError, data: queryData } = useQuery(CLIENT, {
    fetchPolicy: "network-only",
    variables: {
      client_id: account_id,
    },
  });

  const onClear = () => {
    selectInputRef.current.select.select.clearValue();
  };

  const [insertMeeting, { loading: mutationLoading, error: mutationError }] = useMutation(
    INSERT_MEETING,
    {
      onCompleted(data) {
        setSuccessCode(200);
        window.location = "/meetings/edit/" + data.insert_inventory_account_meeting_note_one.id;
      },
      onError() {
        console.log(mutationError);
        setSuccessCode(0);
      },
    }
  );

  const { user } = useAuth0();
  const user_id = user ? user.sub : null;

  const [editorState, setEditorState] = useState(RichTextEditor.createEmptyValue());
  const [inputs, setInputs] = useState({
    meeting_date: new Date().toISOString().substring(0, 16), // For minute precision
  });
  const [attendees, setAttendeesState] = useState({
    inputValue: "",
    value: [],
  });

  const {
    loading: loading_meeting_statues,
    error: error_meeting_statues,
    data: meeting_statues,
  } = useQuery(MEETING_STATUSES, {
    fetchPolicy: "network-only",
  });

  const createOption = label => ({
    label,
    value: label,
  });

  const handleAttendeesChange = (value, actionMeta) => {
    console.log("handleAttendeesChange");
    console.group("Value Changed");
    console.log(value);
    console.log(`action: ${actionMeta.action}`);
    console.groupEnd();
    setAttendeesState({ value, inputValue: attendees.inputValue });
  };
  const handleInputAttendeesChange = inputValue => {
    console.log("handleInputAttendeesChange");
    console.log("attendees");
    console.log(attendees);
    setAttendeesState({ inputValue, value: attendees.value == null ? [] : attendees.value });
  };

  const handleAttendeesKeyDown = event => {
    const { inputValue, value } = attendees;
    console.log("handleAttendeesKeyDown");
    console.log("inputValue");
    console.log(inputValue);
    console.log("value");
    console.log(value);
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab":
        console.group("Value Added");
        console.log(value);
        console.groupEnd();
        setAttendeesState({
          inputValue: "",
          value: [...value, createOption(inputValue)],
        });
        return event.preventDefault();
      default:
        break;
    }
  };

  const handleInputChange = event => {
    event.persist();
    console.log(inputs);

    console.log(editorState.toString("html"));

    setInputs(inputs => ({
      ...inputs,
      [event.target.name]: event.target.value,
    }));
  };

  function arrayToString(array) {
    let values = _.chain(array)
      .map(value => value.value)
      .filter(value => value)
      .map(value => {
        return '"' + value.replace("'", "''").trim() + '"';
      })
      .join(",")
      .value();

    return "{" + values + "}";
  }

  const handleSubmit = event => {
    if (event) {
      event.preventDefault();
    }

    console.log(attendees);
    console.log(inputs);

    // strip any superfluous spaces introduced when pasting from other programs
    const content = editorState.toString("html").replace(/&nbsp;/g, "");

    insertMeeting({
      variables: {
        user_id: user_id,
        contacts: arrayToString(attendees.value),
        meeting_note_status_id: inputs.meeting_status_id,
        meeting_date: inputs.meeting_date,
        content,
        account_id: account_id,
      },
    });
  };

  if (loading_meeting_statues || meeting_statues === null) return null;
  if (queryData == null || queryLoading || queryData.length == 0) return null;

  return (
    <>
      <div className="bg-gray-100 bg-opacity-25">
        <div className="flex flex-row">
          <div className="xl:w-3/12 hidden xl:block">&nbsp;</div>
          <div className="xl:w-9/12 w-full">
            <div className="container max-w-screen-xl lg:pr-8 mt-20 xl:mt-0 leading-normal tracking-normal overflow-x-hidden overflow-y-hidden">
              <div className="flex pt-4 mb-4">
                <div className="w-full h-auto  ml-2">
                  <div className="fixed xl:relative xl:top-auto top-0 pt-8 pb-4 xl:pl-0 w-full bg-white xl:bg-transparent z-50 xl:left-auto left-0 xl:ml-0 pl-16 border-b xl:border-none">
                    <TitleHeading label="Add Meeting" />
                    {successCode === 200 && (
                      <div
                        className="p-2 bg-green-800 items-center text-indigo-100 leading-none lg:rounded-full flex lg:inline-flex"
                        role="alert"
                      >
                        <span className="flex rounded-full bg-green-500 uppercase px-2 py-1 text-xs font-bold mr-3">
                          Success
                        </span>
                        <span className="font-semibold mr-2 text-left flex-auto">
                          The Meeting was added successfully.
                        </span>
                      </div>
                    )}
                    {mutationError && (
                      <div
                        className="p-2 bg-red-800 items-center text-indigo-100 leading-none lg:rounded-full flex lg:inline-flex"
                        role="alert"
                      >
                        <span className="flex rounded-full bg-red-500 uppercase px-2 py-1 text-xs font-bold mr-3">
                          Error
                        </span>
                        <span className="font-semibold mr-2 text-left flex-auto">
                          There was a problem with the data - please check.
                        </span>
                      </div>
                    )}
                  </div>

                  {query.get("account_type") === "supplier" ? (
                    <Link
                      className="text-3xl xl:text-3xl mb-4 text-gray-900 font-bold"
                      target="_blank"
                      rel="noopener noreferrer"
                      to={`/supplier/${account_id}`}
                    >
                      Account Name: {queryData.inventory_account[0].local_name}
                    </Link>
                  ) : (
                    <Link
                      className="text-3xl xl:text-3xl mb-4 text-gray-900 font-bold"
                      target="_blank"
                      rel="noopener noreferrer"
                      to={`/client/${account_id}`}
                    >
                      Account Name: {queryData.inventory_account[0].local_name}
                    </Link>
                  )}

                  <form className="w-full flex flex-col lg:flex-row" onSubmit={handleSubmit}>
                    <div className=" w-full lg:w-1/2 p-4 lg:p-0">
                      <div className="w-8/12 p-4 -mx-3">
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                          Attendees
                        </label>
                        <CreatableSelect
                          ref={selectInputRef}
                          placeholder="Add Attendee"
                          inputValue={attendees.inputValue}
                          isClearable
                          isMulti
                          menuIsOpen={false}
                          onChange={handleAttendeesChange}
                          onInputChange={handleInputAttendeesChange}
                          onKeyDown={handleAttendeesKeyDown}
                          value={attendees.value}
                          menuPortalTarget={document.querySelector("body")}
                        />
                      </div>
                      <div className="flex flex-wrap mb-2">
                        <SimpleDateInput
                          placeholder="Meeting Time"
                          change_handler={handleInputChange}
                          label="Meeting Date"
                          id_ref="meeting_date"
                          value_ref={inputs.meeting_date}
                        />
                      </div>
                      <div className="flex flex-wrap mb-2">
                        <SimpleSelect
                          placeholder="Select Meeting Status"
                          options={meeting_statues.inventory_meeting_note_status}
                          required
                          change_handler={handleInputChange}
                          label="Meeting Status"
                          id_ref="meeting_status_id"
                          name_func={option => {
                            return option.name;
                          }}
                        />
                      </div>
                      <div className="flex flex-wrap mb-2 ">
                        <RichTextEditor value={editorState} onChange={setEditorState} />
                      </div>
                      <SimpleButton label="Add Meeting" />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddMeeting;
